import { Eye, EyeSlash } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useState } from "react";
import { TextField as AriaTextField, Button, Group, Input } from "react-aria-components";
import { cx } from "@/lib/cx";
import { controlHeight, FieldDescription, FieldErrorText, FieldLabel, fieldRoot, inputBase } from "./field";
import type { TextFieldProps } from "./TextField";

export type PasswordFieldProps = Omit<TextFieldProps, "multiline" | "rows" | "type">;

/** Password input with show/hide toggle. Set `autoComplete` to current-password or new-password. */
export function PasswordField({
  label,
  labelHidden,
  description,
  error,
  placeholder,
  className,
  ...props
}: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  return (
    <AriaTextField
      className={cx(fieldRoot, className)}
      type={visible ? "text" : "password"}
      isInvalid={error ? true : props.isInvalid}
      {...props}
    >
      <FieldLabel hidden={labelHidden}>{label}</FieldLabel>
      <Group className="relative flex items-center">
        <Input placeholder={placeholder} className={cx(inputBase, controlHeight, "pr-10")} />
        <Button
          type="button"
          onPress={() => setVisible((v) => !v)}
          aria-label={visible ? m.field_hide_password() : m.field_show_password()}
          aria-pressed={visible}
          className="absolute right-0.5 inline-flex size-(--control-h-sm) items-center justify-center rounded-control text-fg-muted transition-colors duration-(--dur-fast) data-hovered:bg-hover data-hovered:text-fg data-pressed:bg-active"
        >
          {visible ? <EyeSlash size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
        </Button>
      </Group>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <FieldErrorText>{error}</FieldErrorText>
    </AriaTextField>
  );
}
